import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import PropertyCard from "@/components/PropertyCard";
import MapView from "@/components/MapView";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutGrid, Map as MapIcon, LocateFixed, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["casa", "apartamento", "villa", "local", "terreno", "oficina"];
const RADIUS_OPTIONS = [5, 10, 25, 50, 100];
const DEFAULT_RADIUS = 25;

export default function Properties() {
  const { t } = useLocale();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => (params.get("view") === "map" ? "map" : "list"));
  const [locating, setLocating] = useState(false);

  const filters = useMemo(
    () => ({
      property_type: params.get("type") || "",
      transaction_type: params.get("op") || "",
      min_price: params.get("min") || "",
      max_price: params.get("max") || "",
      bedrooms: params.get("beds") || "",
      q: params.get("q") || "",
      lat: params.get("lat") ? parseFloat(params.get("lat")) : null,
      lng: params.get("lng") ? parseFloat(params.get("lng")) : null,
      radius_km: params.get("radius") ? parseFloat(params.get("radius")) : null,
    }),
    [params]
  );

  const [draft, setDraft] = useState(filters);
  useEffect(() => { setDraft(filters); }, [filters]);

  const userLocation = filters.lat != null && filters.lng != null
    ? { lat: filters.lat, lng: filters.lng }
    : null;

  // Auto-switch to map view when Near-me is activated
  useEffect(() => {
    if (userLocation && view !== "map") setView("map");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation ? `${userLocation.lat},${userLocation.lng}` : null]);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (filters.property_type) q.property_type = filters.property_type;
    if (filters.transaction_type) q.transaction_type = filters.transaction_type;
    if (filters.min_price) q.min_price = filters.min_price;
    if (filters.max_price) q.max_price = filters.max_price;
    if (filters.bedrooms) q.bedrooms = filters.bedrooms;
    if (filters.q) q.q = filters.q;
    if (filters.lat != null && filters.lng != null && filters.radius_km) {
      q.lat = filters.lat;
      q.lng = filters.lng;
      q.radius_km = filters.radius_km;
    }
    api.get("/properties", { params: q })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filters]);

  const patchParams = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    setParams(next);
  };

  const apply = () => {
    patchParams({
      type: draft.property_type,
      op: draft.transaction_type,
      min: draft.min_price,
      max: draft.max_price,
      beds: draft.bedrooms,
      q: draft.q,
    });
  };

  const clear = () => {
    setParams({});
    setDraft({ property_type: "", transaction_type: "", min_price: "", max_price: "", bedrooms: "", q: "", lat: null, lng: null, radius_km: null });
  };

  const requestNearMe = () => {
    if (!("geolocation" in navigator)) {
      toast.error(t("filters.near_me_unsupported"));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const radius = filters.radius_km || DEFAULT_RADIUS;
        patchParams({ lat, lng, radius });
        setView("map");
        toast.success(t("filters.near_me_active").replace("{km}", radius));
      },
      (err) => {
        setLocating(false);
        toast.error(t("filters.near_me_denied"));
        console.error(err);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const clearNearMe = () => {
    patchParams({ lat: null, lng: null, radius: null });
  };

  const changeRadius = (value) => {
    const r = parseFloat(value);
    if (filters.lat != null && filters.lng != null) {
      patchParams({ radius: r });
    } else {
      // No coords yet — just remember the choice locally
      setDraft((d) => ({ ...d, radius_km: r }));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-10 pt-8 md:pt-20 pb-16">
      <div className="mb-8 md:mb-10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">{t("nav.properties")}</div>
          <h1 className="mt-2 font-serif text-4xl md:text-6xl">{t("filters.title")}</h1>
        </div>
        <div className="inline-flex rounded-full border border-border p-1 bg-background" data-testid="view-toggle">
          <button
            type="button"
            onClick={() => setView("list")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground"}`}
            data-testid="view-list-btn"
          >
            <LayoutGrid className="h-4 w-4" /> {t("filters.view_list")}
          </button>
          <button
            type="button"
            onClick={() => setView("map")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${view === "map" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground"}`}
            data-testid="view-map-btn"
          >
            <MapIcon className="h-4 w-4" /> {t("filters.view_map")}
          </button>
        </div>
      </div>

      {/* Near-me action bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {userLocation ? (
          <>
            <span
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm"
              data-testid="near-me-chip"
            >
              <LocateFixed className="h-4 w-4" />
              {t("filters.near_me_active").replace("{km}", filters.radius_km || DEFAULT_RADIUS)}
              <button
                type="button"
                onClick={clearNearMe}
                className="ml-1 inline-flex items-center justify-center rounded-full hover:bg-primary-foreground/20 p-0.5"
                aria-label={t("filters.near_me_clear")}
                data-testid="near-me-clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
            <div className="inline-flex items-center gap-2">
              <span className="label-eyebrow">{t("filters.radius")}</span>
              <Select value={String(filters.radius_km || DEFAULT_RADIUS)} onValueChange={changeRadius}>
                <SelectTrigger className="w-[110px] h-9" data-testid="near-me-radius">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RADIUS_OPTIONS.map((r) => (
                    <SelectItem key={r} value={String(r)}>{r} km</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        ) : (
          <Button
            type="button"
            onClick={requestNearMe}
            disabled={locating}
            variant="outline"
            className="rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground btn-hover-lift"
            data-testid="near-me-btn"
          >
            {locating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LocateFixed className="h-4 w-4 mr-2" />}
            {locating ? t("filters.near_me_locating") : t("filters.near_me")}
          </Button>
        )}
      </div>

      <div className="border border-border p-5 md:p-8 mb-10 grid gap-4 md:grid-cols-6 rounded-lg bg-card">
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-2">{t("filters.search")}</label>
          <Input
            data-testid="filter-search"
            value={draft.q}
            onChange={(e) => setDraft({ ...draft, q: e.target.value })}
            placeholder="Punta Cana, Santo Domingo…"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("filters.type")}</label>
          <Select value={draft.property_type || "any"} onValueChange={(v) => setDraft({ ...draft, property_type: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="filter-type"><SelectValue placeholder={t("filters.any")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("filters.any")}</SelectItem>
              {TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`types.${tp}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("filters.transaction")}</label>
          <Select value={draft.transaction_type || "any"} onValueChange={(v) => setDraft({ ...draft, transaction_type: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="filter-op"><SelectValue placeholder={t("filters.any")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("filters.any")}</SelectItem>
              <SelectItem value="venta">{t("filters.sale")}</SelectItem>
              <SelectItem value="alquiler">{t("filters.rent")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("filters.bedrooms")}</label>
          <Input
            data-testid="filter-beds"
            type="number" min="0"
            value={draft.bedrooms}
            onChange={(e) => setDraft({ ...draft, bedrooms: e.target.value })}
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("filters.min_price")}</label>
          <Input data-testid="filter-min" type="number" min="0" value={draft.min_price}
                 onChange={(e) => setDraft({ ...draft, min_price: e.target.value })} />
        </div>
        <div className="md:col-span-6 flex flex-wrap gap-3 pt-2">
          <Button onClick={apply} data-testid="filter-apply" className="rounded-full">{t("filters.apply")}</Button>
          <Button onClick={clear} variant="ghost" data-testid="filter-clear">{t("filters.clear")}</Button>
          <span className="ml-auto self-center text-sm text-muted-foreground">
            {items.length} {t("filters.results")}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground text-sm">{t("common.loading")}</div>
      ) : view === "map" ? (
        <MapView
          properties={items}
          height="72vh"
          userLocation={userLocation}
          radiusKm={userLocation ? (filters.radius_km || DEFAULT_RADIUS) : null}
        />
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground text-sm">— sin resultados —</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14" data-testid="properties-grid">
          {items.map((p, i) => <PropertyCard key={p.property_id} property={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
